import {
    ColumnTypes,
    InternalColumnTypes,
} from "../../../components/stateful-content/stateful-table/column-types";

export interface ContentData<T = any> {
    /** url for next page of data */
    nextPage?: string;
    /** @deprectated number of filtered results. Use displayedCount instead */
    filteredCount?: number;
    /** number of displayed results */
    displayedCount?: number;
    /** number of total results */
    totalCount: number;
    /** string with service call message */
    messages?: ServiceCallMessages;
    /** array with result items */
    items: Array<T>;
    /** schema used to render the according view */
    schema?: ContentDataSchema;
    /** @deprecated optional error. Use translations instead. */
    error?: ContentApiError;
    /** @deprecated Label used for displaying result summary. Use translations instead. */
    labelCount?: string;
    /** if set to true the user can't create service cases from the tiles */
    isCreateCaseAllowed?: boolean;
    /** indicates that the auth-image component should be used to display the images (Asset Management) */
    authImages?: boolean;
}

export interface ContentDataProviderState<T = any> {
    /** The last api request data */
    readonly data: ContentData<T> | null;
    /** Api endpoint */
    readonly endpoint: string | null;
    /** The last api request error */
    readonly error: ContentApiError | null;
    /** Flag that indicates if a request is pending */
    readonly pending: boolean;
    /** Query params for the api request */
    readonly query: string;
    /** Sort query param */
    readonly sort: string;
}

export interface ContentDataProvider<T = any>
    extends ContentDataProviderState<T> {
    /** Get data asyncronously */
    getData(): Promise<ContentData<T> | null>;
    /** Set api endpoint url */
    setEndpoint(endpoint: string): void;
    /** Set query string */
    setQuery(query: string): void;
    /** Set query string */
    setQueryParam(key: string, value: string): void;
    /** Set sort query parameter (e.g. "name-asc") */
    setSortParam(value: string): void;
    /** Get a single query parameter */
    getQueryParam(key: string): string | null;
    /** load next page */
    loadMore(): void;
    /** load first page */
    loadFirstPage(): void;
    /** The current state */
    readonly state: ContentDataProviderState<T>;
    /** Subscribe to state changes (returns unsubscribe callback) */
    readonly subscribe: (handler: ContentDataProviderCallback<T>) => () => void;
    /** Unsubscribe a  */
    readonly unsubscribe: (handler: ContentDataProviderCallback<T>) => void;
}

export interface ContentDataProviderCallback<T = any> {
    /** Handler for state changes */
    (state: ContentDataProviderState<T>): void;
}

export interface ContentResponse<T = any> extends Response {
    /** Overrides the the native Response interface with data signature */
    json: () => Promise<ContentData<T>>;
}

export interface ServiceCallMessages {
    unreadChatMessagesText: string;
}

export interface ContentDataSchema {
    /** Label for the data field */
    [field: string]: ContentDataSchemaField;
}

export interface ContentDataSchemaField extends Record<string, any> {
    type?: ColumnTypes | InternalColumnTypes;
    /** Label for the data field */
    label?: string;
    /** @deprecated Set of labels for the field if used without translations */
    labels?: Record<string, string>;
    /** Flag that indicates if data can be sorted by this field */
    sortable?: boolean;
}

export interface ContentApiError {
    /** Status code of the response */
    status?: number;
    /** Error message */
    message?: string;
    /** deprecated: Optional error headline. Use headline instead */
    head?: string;
    /** Optional error headline */
    headline?: string;
    /** deprecated: Optional error copy text. Use text instead */
    copy?: string;
    /** Optional error copy text */
    text?: string;
    /** Optional error icon */
    icon?: {
        name?: string;
        size?: string;
        weight?: string;
        color?: string;
    };
}
